import { TripDetails, Itinerary } from '../../types/itinerary';
import { itineraryService, dataSyncService } from '../../services/dataPersistence';

class StreamParser {
  private buffer: string = '';
  private jsonBuffer: string = '';
  private isCollectingJSON: boolean = false;
  private objects: any[] = [];
  private streamingActivityDetected: boolean = false;

  process(chunk: string): { 
    objects: any[], 
    streamingActivity: boolean 
  } {
    
    // Add new chunk to buffer
    this.buffer += chunk;
    
    // Process complete SSE lines
    const lines = this.buffer.split('\n');
    this.buffer = lines.pop() || '';
    
    // Process each complete line
    for (const line of lines) {
      if (!line.trim()) continue;
      if (!line.startsWith('data: ')) continue;
      
      const content = line.slice(6);
      if (content === '[DONE]') continue;
      
      try {
        const parsed = JSON.parse(content);
        if (parsed.text) {
          // Check for activity type start or any activity-related content
          if (
            parsed.text.includes('"type": "activity"') || 
            parsed.text.includes('"dayNumber":') ||
            parsed.text.includes('"startTime":')
          ) {
            this.streamingActivityDetected = true;
          }
          this.processText(parsed.text);
        }
      } catch (e) {
        console.warn('[StreamParser] Error parsing SSE data:', e);
      }
    }
    
    // Return complete objects and streaming status
    const completeObjects = [...this.objects];
    const streamingStatus = this.streamingActivityDetected;
    this.objects = [];
    
    // Only reset streaming detection after we've found a complete object
    if (completeObjects.length > 0) {
      this.streamingActivityDetected = false;
    }

    return {
      objects: completeObjects,
      streamingActivity: streamingStatus
    };
  }

  private processText(text: string) {
    this.jsonBuffer += text;
    
    while (true) {
      const result = this.extractNextJsonObject(this.jsonBuffer);
      if (!result) break;
      
      const { jsonObject, remainingText } = result;
      
      try {
        const parsed = JSON.parse(jsonObject);
        if (parsed.type && parsed.data) {
          this.objects.push(parsed);
        }
      } catch (e) {
        console.warn('[StreamParser] Error parsing JSON object:', e);
      }
      
      this.jsonBuffer = remainingText.trim();
    }
  }

  private extractNextJsonObject(text: string): { jsonObject: string, remainingText: string } | null {
    let bracketCount = 0;
    let inString = false;
    let escapeNext = false;
    let startIndex = -1;

    startIndex = text.indexOf('{');
    if (startIndex === -1) return null;

    for (let i = startIndex; i < text.length; i++) {
      const char = text[i];

      if (escapeNext) {
        escapeNext = false;
        continue;
      }

      if (char === '\\' && inString) {
        escapeNext = true;
        continue;
      }

      if (char === '"' && !escapeNext) {
        inString = !inString;
        continue;
      }

      if (!inString) {
        if (char === '{') {
          bracketCount++;
        } else if (char === '}') {
          bracketCount--;
          
          if (bracketCount === 0) {
            const jsonObject = text.slice(startIndex, i + 1);
            const remainingText = text.slice(i + 1);
            
            return { jsonObject, remainingText };
          }
        }
      }
    }

    return null;
  }

  reset() {
    this.buffer = '';
    this.jsonBuffer = '';
    this.isCollectingJSON = false;
    this.objects = [];
    this.streamingActivityDetected = false;
  }
}    

// Calculate total days between two dates
function calculateTotalDays(tripDetails: TripDetails): number {
  if (!tripDetails.startDate || !tripDetails.endDate) {
    return 0;
  }
  
  return Math.ceil(
    (tripDetails.endDate.getTime() - tripDetails.startDate.getTime()) / (1000 * 60 * 60 * 24)
  ) + 1;
}

// Generate a compact summary of activities from previous days
function generatePreviousContext(itinerary: Partial<Itinerary>, upToDay: number): string {
  if (!itinerary.days || upToDay <= 0) {
    return '';
  }
  
  const relevantDays = itinerary.days.filter(day => day.dayNumber <= upToDay && day.activities.length > 0);
  if (relevantDays.length === 0) {
    return '';
  }
  
  const dayContexts = relevantDays.map(day => {
    const activitySummaries = day.activities
      .slice(0, 3) // Limit to 3 activities per day to keep prompt size manageable
      .map(activity => 
        `${activity.startTime} - ${activity.name} at ${activity.location.name}`
      ).join('; ');
    
    return `Day ${day.dayNumber}: ${activitySummaries}`;
  });
  
  return `The following itinerary has already been generated:\n${dayContexts.join('\n')}\n\n`;
}

// Process a single chunk API call with streaming response
async function processChunk_UNUSED(
  prompt: string,
  currentItinerary: Partial<Itinerary>,
  onUpdate?: (partialItinerary: Partial<Itinerary>, streamingActivity?: boolean, activeDay?: number) => void
): Promise<void> {
  // Check if we're using the production API URL (starts with https)
  const isProductionApiUrl = import.meta.env.VITE_API_URL?.startsWith('https');
  // Use relative URL for production, full URL for development
  const API_URL = isProductionApiUrl
    ? '/api/itinerary-builder'
    : import.meta.env.VITE_API_URL + '/api/itinerary-builder';
  
  console.log('[DEBUG] Sending chunk prompt to API');
  
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream'
    },
    body: JSON.stringify({
      messages: [{
        role: 'user',
        content: prompt
      }]
    })
  });

  if (!response.ok) {
    throw new Error(`Failed to generate itinerary chunk: ${response.status}`);
  }

  // Track the active day being updated
  let activeDay: number | undefined = undefined;
  
  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body');

  const decoder = new TextDecoder();
  const parser = new StreamParser();
  
  console.log('[DEBUG] Starting to read response stream for this chunk...');
  
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    const chunk = decoder.decode(value, { stream: true });
    const { objects, streamingActivity } = parser.process(chunk);
    
    for (const obj of objects) {
      switch (obj.type) {
        case 'meta':
          // Only update meta information if not already set
          if (!currentItinerary.tripDetails?.destination) {
            currentItinerary.tripDetails = {
              ...currentItinerary.tripDetails,
              ...obj.data
            };
          }
          break;

        case 'activity':
          const activity = obj.data;

          console.log(`[DEBUG] Activity received for day ${activity.dayNumber}: ${activity.name}`);

          // Update the active day to the current activity's day
          activeDay = activity.dayNumber;
          
          const dayIndex = currentItinerary.days?.findIndex(
            day => day.dayNumber === activity.dayNumber
          );

          if (dayIndex !== undefined && dayIndex !== -1 && currentItinerary.days) {
            const day = currentItinerary.days[dayIndex];
            const activities = [...(day.activities || [])];
            
            const existingIndex = activities.findIndex(a => a.id === activity.id);
            if (existingIndex !== -1) {
              activities[existingIndex] = activity;
            } else {
              activities.push(activity);
            }

            currentItinerary.days[dayIndex] = {
              ...day,
              activities: activities.sort((a, b) => {
                const timeA = a.startTime.split(':').map(Number);
                const timeB = b.startTime.split(':').map(Number);
                return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
              })
            };
          }
          break;

        case 'budget':
          currentItinerary.budgetSummary = obj.data;
          // Budget typically comes last, so reset active day
          activeDay = undefined;
          break;
      }
      
      // Update UI with latest data
      onUpdate?.(currentItinerary, streamingActivity, activeDay);
    }
  }
  
  // Final update after processing this chunk
  onUpdate?.(currentItinerary, false, undefined);
  console.log('[DEBUG] Completed processing chunk');
}

async function processChunk(
  prompt: string,
  currentItinerary: Partial<Itinerary>,
  chunkStart: number,
  chunkEnd: number,
  onUpdate?: (partialItinerary: Partial<Itinerary>, streamingActivity?: boolean, activeDay?: number) => void
): Promise<void> {
  const isProductionApiUrl = import.meta.env.VITE_API_URL?.startsWith('https');
  const API_URL = isProductionApiUrl
    ? '/api/itinerary-builder'
    : import.meta.env.VITE_API_URL + '/api/itinerary-builder';
  
  console.log(`[DEBUG] Processing chunk days ${chunkStart}-${chunkEnd}`);
  
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream'
    },
    body: JSON.stringify({
      messages: [{
        role: 'user',
        content: prompt
      }]
    })
  });

  if (!response.ok) {
    throw new Error(`Failed to generate itinerary chunk: ${response.status}`);
  }

  let activeDay: number | undefined = undefined;
  
  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body');

  const decoder = new TextDecoder();
  const parser = new StreamParser();
  const processedDays = new Set<number>();
  const activitiesPerDay = new Map<number, number>();
  
  console.log('[DEBUG] Starting to read response stream for this chunk...');
  
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    const chunk = decoder.decode(value, { stream: true });
    
    //console.log(`[DEBUG] Chunk ${chunkStart}-${chunkEnd}: Raw chunk length: ${chunk.length}`);

    const { objects, streamingActivity } = parser.process(chunk);
    
    for (const obj of objects) {
      switch (obj.type) {
        case 'meta':
          if (!currentItinerary.tripDetails?.destination) {
            currentItinerary.tripDetails = {
              ...currentItinerary.tripDetails,
              ...obj.data
            };
          }
          break;

        case 'activity':
          const activity = obj.data;
          const dayNum = activity.dayNumber;
          
          // Validate day number is in expected range
          if (dayNum < chunkStart || dayNum > chunkEnd) {
            console.warn(`[DEBUG] Activity for day ${dayNum} outside chunk range ${chunkStart}-${chunkEnd}`);
            continue;
          }
          
          activeDay = dayNum;
          processedDays.add(dayNum);
          activitiesPerDay.set(dayNum, (activitiesPerDay.get(dayNum) || 0) + 1);

          // At the end of streaming, before validation:
          console.log(`[DEBUG] Stream ended. Total activities by day:`, activitiesPerDay);
          console.log(`[DEBUG] Last chunk received:`, chunk.slice(-200)); // Last 200 chars
          
          const dayIndex = currentItinerary.days?.findIndex(
            day => day.dayNumber === dayNum
          );

          if (dayIndex !== undefined && dayIndex !== -1 && currentItinerary.days) {
            const day = currentItinerary.days[dayIndex];
            const activities = [...(day.activities || [])];
            
            const existingIndex = activities.findIndex(a => a.id === activity.id);
            if (existingIndex !== -1) {
              activities[existingIndex] = activity;
            } else {
              activities.push(activity);
            }

            currentItinerary.days[dayIndex] = {
              ...day,
              activities: activities.sort((a, b) => {
                const timeA = a.startTime.split(':').map(Number);
                const timeB = b.startTime.split(':').map(Number);
                return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
              })
            };
          }

          break;

        case 'budget':
          currentItinerary.budgetSummary = obj.data;
          activeDay = undefined;
          break;
      }

      console.log('Streaming activity ---- ',streamingActivity)
      
      onUpdate?.(currentItinerary, streamingActivity, activeDay);
    }
  }

  // Validate all days have activities
  const expectedDays = Array.from(
    {length: chunkEnd - chunkStart + 1}, 
    (_, i) => chunkStart + i
  );
  const missingDays = expectedDays.filter(day => !processedDays.has(day));
  const insufficientDays = expectedDays.filter(day => {
    const count = activitiesPerDay.get(day) || 0;
    return count > 0 && count < 2; // Flag days with less than 2 activities
  });

  if (missingDays.length > 0) {
    console.error(`[DEBUG] Missing days: ${missingDays}, Activities per day:`, Object.fromEntries(activitiesPerDay));
    throw new Error(`Incomplete response - missing days: ${missingDays.join(', ')}`);
  }
  
  if (insufficientDays.length > 0) {
    console.warn(`[DEBUG] Days with insufficient activities (<2): ${insufficientDays.join(', ')}`);
  }

  onUpdate?.(currentItinerary, false, undefined);
  console.log('[DEBUG] Completed processing chunk');
}

// 2. Improve generatePrompt to be more explicit about day requirements
function generatePrompt(
  tripDetails: TripDetails, 
  startDay: number = 1, 
  endDay: number = -1, 
  totalDays: number = -1, 
  previousDaysContext: string = '',
  includeBudget: boolean = true
): string {
  // Calculate day count if not provided
  if (totalDays === -1 && tripDetails.startDate && tripDetails.endDate) {
    totalDays = Math.ceil(
      (tripDetails.endDate.getTime() - tripDetails.startDate.getTime()) / (1000 * 60 * 60 * 24)
    ) + 1;
  }
  
  // If endDay is not specified, use all days
  if (endDay === -1) {
    endDay = totalDays;
  }

  const daysToGenerate = Array.from(
    {length: endDay - startDay + 1}, 
    (_, i) => startDay + i
  ).join(', ');

  // Base prompt
  let prompt = `Create a detailed travel itinerary for ${tripDetails.destination} with these details:
  1. Travel Group: ${tripDetails.travelGroup}
  ${tripDetails.budget ? `2. Budget: ${tripDetails.budget}\n` : ''}
  ${tripDetails.startDate && tripDetails.endDate ? `3. Dates: ${tripDetails.startDate.toLocaleDateString()} to ${tripDetails.endDate.toLocaleDateString()} (${totalDays} days)\n` : ''}
  ${tripDetails.preferences?.physicalAbility ? `4. Activity Level: ${tripDetails.preferences.physicalAbility}\n` : ''}
  ${tripDetails.preferences?.activityTypes?.length ? `5. Interests: ${tripDetails.preferences.activityTypes.join(', ')}\n` : ''}
  
  ${previousDaysContext ? `${previousDaysContext}\n` : ''}
  
  IMPORTANT: You MUST generate activities for THESE SPECIFIC DAYS: ${daysToGenerate}
  
  Return a series of JSON objects in this exact format:
  `;

  // Only include meta object if starting from day 1
  if (startDay === 1) {
    prompt += `
  First send the meta information:
  {
    "type": "meta",
    "data": {
      "destination": string,
      "startDate": string,
      "endDate": string,
      "travelGroup": string
    }
  }
  `;
  }

  // Activities section with explicit day list
  prompt += `
  ${startDay === 1 ? 'Then send' : 'Send'} activities for days ${daysToGenerate}:
  - Generate AT LEAST 3-4 activities for EACH of these days: ${daysToGenerate}
  - Each activity MUST include "dayNumber": ${startDay <= endDay ? `${startDay}, ${startDay + 1}, ... up to ${endDay}` : startDay}
  
  {
    "type": "activity",
    "data": {
      "id": string,
      "dayNumber": number,  // MUST be one of: ${daysToGenerate}
      "date": string,
      "name": string,
      "location": {
        "id": string,
        "name": string,
        "city": string,
        "country": string,
        "position": {
          "lat": number,
          "lng": number
        },
        "rating": number,
        "reviews": number
      },
      "startTime": string,
      "duration": string,
      "transport": string,
      "travelTime": string,
      "cost": string,
      "description": string
    }
  }
  `;

  // Only include budget in the final chunk if requested
  if (includeBudget && endDay === totalDays) {
    prompt += `
      Finally send the budget:
      {
        "type": "budget",
        "data": {
          "totalEstimatedBudget": string,
          "categoryBreakdown": {
            "attractions": string,
            "foodAndDining": string,
            "transportation": string,
            "shoppingAndMisc": string,
            "buffer": string
          }
        }
      }`;
  }

  // Critical rules with explicit validation
  prompt += `
  CRITICAL VALIDATION RULES:
  - YOU MUST GENERATE ACTIVITIES FOR ALL THESE DAYS: ${daysToGenerate}
  - BEFORE FINISHING, COUNT: Have you created activities for days ${daysToGenerate}? If not, add them now.
  - Each day (${daysToGenerate}) must have AT LEAST 3 activities
  - DO NOT SKIP ANY DAY NUMBER
  - Keep activity descriptions concise (NO MORE THAN 20 words max per activity). Minimize JSON formatting.
  - If you're about to finish without activities for any of days ${daysToGenerate}, STOP and add them
  
  ACTIVITY RULES:
  - Each object must be complete and valid JSON
  - Create a FULL DAY itinerary with 4-5 activities per day MAX not more than that
  - Start times should range from ~8:00-9:00 (morning) to ~17:00-18:00 (evening)
  - Include meals at appropriate times
  - Group activities by geographic proximity within each day
  - All activities within a single day must be within a 10km radius of each other
  - OR: Maximum 5km distance between any two consecutive activities
  - Group activities by neighborhood/district to minimize travel
  - Group morning activities in one area, afternoon in another if needed
  - Explicitly name the neighborhood/district for each activity cluster
  - Use realistic durations (30 mins - 3 hours)
  - Use 24-hour format for times (HH:MM)
  - Use USD with $ prefix for costs
  - Each activity and location must have unique IDs
  - All ratings must be between 1-5
  - All coordinates must be valid (lat: -90 to 90, lng: -180 to 180)
  - Generate location IDs with descriptive prefixes (e.g., "loc-temple-abc123", "loc-restaurant-def456")
  - Generate activity IDs with unique prefixes (e.g., "act-morning-xyz789")
  ${previousDaysContext ? `- Do not repeat activities from previous days` : ''}`;

  prompt += `\n\nFINAL CHECK: Have you generated activities for ALL days ${daysToGenerate}? If not, continue generating.`;

  return prompt;
}

// 3. Enhanced retry logic with fallback
export async function generateItinerary(
  tripDetails: TripDetails,
  onUpdate?: (partialItinerary: Partial<Itinerary>, streamingActivity?: boolean, activeDay?: number) => void,
  daysPerChunk: number = 3 // Reduced from default to minimize missing days
): Promise<Itinerary> {
  const itineraryId = `itinerary_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  console.log(`[DEBUG] Creating new itinerary with ID: ${itineraryId}`);
  
  const currentItinerary: Partial<Itinerary> = {
    id: itineraryId,
    tripDetails: {
      destination: tripDetails.destination,
      startDate: tripDetails.startDate?.toISOString(),
      endDate: tripDetails.endDate?.toISOString(),
      travelGroup: tripDetails.travelGroup
    },
    days: [],
    budgetSummary: {
      totalEstimatedBudget: 'Calculating...',
      categoryBreakdown: {
        attractions: 'Calculating...',
        foodAndDining: 'Calculating...',
        transportation: 'Calculating...',
        shoppingAndMisc: 'Calculating...',
        buffer: 'Calculating...'
      }
    }
  };  

  const totalDays = calculateTotalDays(tripDetails);
  
  if (tripDetails.startDate && tripDetails.endDate) {
    currentItinerary.days = Array.from({ length: totalDays }, (_, i) => {
      const date = new Date(tripDetails.startDate!);
      date.setDate(date.getDate() + i);
      return {
        date: date.toISOString(),
        dayNumber: i + 1,
        activities: []
      };
    });
  }

  if (onUpdate) {
    onUpdate(currentItinerary, false, undefined);
  }

  try {
    // Process in smaller chunks to reduce missing days
    const chunkSize = Math.min(daysPerChunk, 2); // Max 2 days per chunk for reliability
    
    for (let chunkStart = 1; chunkStart <= totalDays; chunkStart += chunkSize) {
      const chunkEnd = Math.min(chunkStart + chunkSize - 1, totalDays);
      const isFinalChunk = chunkEnd >= totalDays;
      
      //console.log(`[DEBUG] Processing days ${chunkStart}-${chunkEnd} of ${totalDays}`);
      
      const previousDaysContext = generatePreviousContext(currentItinerary, chunkStart - 1);
      
      let retries = 0;
      const maxRetries = 3; // Increased retries
      let lastError: Error | null = null;

      while (retries <= maxRetries) {
        try {
          const chunkPrompt = generatePrompt(
            tripDetails, 
            chunkStart, 
            chunkEnd, 
            totalDays,
            previousDaysContext + (retries > 0 ? `\n\nATTEMPT ${retries + 1}: Previous attempts failed to generate all days. YOU MUST include activities for days ${chunkStart} through ${chunkEnd}.` : ''),
            isFinalChunk
          );
          
          await processChunk(
            chunkPrompt, 
            currentItinerary,
            chunkStart,
            chunkEnd,
            onUpdate
          );
          break; // Success
        } catch (error) {
          lastError = error as Error;
          retries++;
          if (retries > maxRetries) {
            console.error(`[DEBUG] Failed after ${maxRetries} retries:`, error);
            // Instead of throwing, add placeholder activities for missing days
            for (let day = chunkStart; day <= chunkEnd; day++) {
              const dayIndex = currentItinerary.days?.findIndex(d => d.dayNumber === day);
              if (dayIndex !== undefined && dayIndex !== -1 && currentItinerary.days![dayIndex].activities.length === 0) {
                console.warn(`[DEBUG] Adding placeholder for day ${day}`);
                // Add minimal placeholder activities
                currentItinerary.days![dayIndex].activities = [{
                  id: `placeholder-${day}-1`,
                  dayNumber: day,
                  date: currentItinerary.days![dayIndex].date,
                  name: 'Day planning in progress',
                  location: {
                    id: `loc-placeholder-${day}`,
                    name: tripDetails.destination,
                    city: tripDetails.destination,
                    country: '',
                    position: { lat: 0, lng: 0 },
                    rating: 0,
                    reviews: 0
                  },
                  startTime: '09:00',
                  duration: '8 hours',
                  transport: 'Various',
                  travelTime: '0 mins',
                  cost: 'TBD',
                  description: 'Activities for this day are being generated. Please try regenerating the itinerary if this persists.'
                }];
              }
            }
            break;
          }
          console.log(`[DEBUG] Retry ${retries}/${maxRetries} for chunk ${chunkStart}-${chunkEnd}`);
          await new Promise(resolve => setTimeout(resolve, 1000 * retries)); // Exponential backoff
        }
      }
      
      if (!isFinalChunk) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    // Ensure budget is properly set
    currentItinerary.budgetSummary = {
      totalEstimatedBudget: currentItinerary.budgetSummary?.totalEstimatedBudget || "$0",
      categoryBreakdown: {
        attractions: currentItinerary.budgetSummary?.categoryBreakdown?.attractions || "$0",
        foodAndDining: currentItinerary.budgetSummary?.categoryBreakdown?.foodAndDining || "$0",
        transportation: currentItinerary.budgetSummary?.categoryBreakdown?.transportation || "$0",
        shoppingAndMisc: currentItinerary.budgetSummary?.categoryBreakdown?.shoppingAndMisc || "$0",
        buffer: currentItinerary.budgetSummary?.categoryBreakdown?.buffer || "$0"
      }
    };

    console.log('[BUDGET DEBUG] Standardized budget:', JSON.stringify(currentItinerary.budgetSummary));
    // First, add logging in builder.ts to confirm event is dispatched:
    console.log('[Builder] Dispatching budget started event');
    window.dispatchEvent(new CustomEvent('itinerary-budget-started'));
    
    await itineraryService.saveCompleteItinerary(currentItinerary);
    return currentItinerary as Itinerary;
  } catch (error) {
    console.error('[DEBUG] Error generating itinerary:', error);
    throw error;
  }
}

// Function to build itinerary from complete data
export async function buildItineraryFromComplete(
  itineraryData: any,
  onUpdate?: (partialItinerary: Partial<Itinerary>, isStreaming?: boolean, activeDay?: number) => void
): Promise<Itinerary> {
  console.log('[ItineraryBuilder] Building itinerary from complete data:', itineraryData);
  try {      
    // Generate a unique ID if not provided
    const id = itineraryData.id || `itinerary_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    
    // First ensure we have properly structured data with no undefined fields
    const processedData = {
      id,
      tripDetails: {
        destination: itineraryData.tripDetails?.destination || 'Unknown Destination',
        startDate: itineraryData.tripDetails?.startDate || new Date().toISOString(),
        endDate: itineraryData.tripDetails?.endDate || new Date().toISOString(),
        travelGroup: itineraryData.tripDetails?.travelGroup || 'Solo traveler'
      },
      budgetSummary: itineraryData.budgetSummary || {
        totalEstimatedBudget: '$1000',
        categoryBreakdown: {
          attractions: '$250',
          foodAndDining: '$300',
          transportation: '$200',
          shoppingAndMisc: '$150',
          buffer: '$100'
        }
      }
    };
    
    // Create skeleton itinerary structure to start with
    const itinerary: Partial<Itinerary> = {
      ...processedData,
      days: []
    };
    
    // Send initial update with trip details but no days yet
    console.log('[ItineraryBuilder] Sending initial itinerary structure');
    onUpdate?.(itinerary, false);
    
    // Create days array even if none exists in saved data
    let daysToProcess = [];
    
    if (itineraryData.days && Array.isArray(itineraryData.days) && itineraryData.days.length > 0) {
      console.log(`[ItineraryBuilder] Using ${itineraryData.days.length} saved days`);
      daysToProcess = itineraryData.days;
    } else {
      // Calculate day count from dates
      const startDate = new Date(processedData.tripDetails.startDate);
      const endDate = new Date(processedData.tripDetails.endDate);
      const dayCount = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      
      console.log(`[ItineraryBuilder] No days found, generating ${dayCount} empty days`);
      
      // Generate placeholder days with proper structure
      for (let i = 0; i < dayCount; i++) {
        const date = new Date(startDate);
        date.setDate(date.getDate() + i);
        
        daysToProcess.push({
          dayNumber: i + 1,
          date: date.toISOString(),
          activities: []
        });
      }
    }
    
    // Wait a moment before starting to add days
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Process days one by one
    for (const day of daysToProcess) {
      // Ensure day has proper structure with no undefined values
      const processedDay = {
        dayNumber: day.dayNumber || 1,
        date: day.date || new Date().toISOString(),
        activities: Array.isArray(day.activities) ? day.activities : []
      };
      
      // Add this day to our itinerary
      itinerary.days = [...(itinerary.days || []), processedDay];
      
      // Log what we're adding
      console.log(`[ItineraryBuilder] Adding day ${processedDay.dayNumber} with ${processedDay.activities.length} activities`);
      
      // Update UI with streaming flag to show animation
      onUpdate?.(itinerary, true, processedDay.dayNumber);
      
      // Slight delay for visual effect
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Final complete update
    console.log('[ItineraryBuilder] Sending final complete update with days:', itinerary.days?.length);
    onUpdate?.(itinerary, false);
    
    // At the end of the function, only save if it's a new itinerary
    if (!itineraryData.id) {
      // Only save for new itineraries, not when rebuilding
      itineraryService.updateItinerary(id, itinerary as any);
    }
    itineraryService.setCurrentItinerary(id);
    
    return itinerary as Itinerary;
  } catch (error) {
    console.error('[ItineraryBuilder] Error building itinerary:', error);
    throw error;
  }
}

export async function simulateGenerateItinerary(
  tripDetails: TripDetails & { savedItineraryId?: string },
  onUpdate?: (partialItinerary: Partial<Itinerary>, streamingActivity?: boolean, activeDay?: number) => void
): Promise<Itinerary> {
  console.log('[ItineraryLoader] Simulating itinerary generation for:', tripDetails);
  // Create initial skeleton structure (rest of the existing code)
  const currentItinerary: Partial<Itinerary> = {
    id: `itinerary_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    tripDetails: {
      destination: tripDetails.destination,
      startDate: tripDetails.startDate?.toISOString(),
      endDate: tripDetails.endDate?.toISOString(),
      travelGroup: tripDetails.travelGroup
    },
    days: [],
    budgetSummary: {
      totalEstimatedBudget: 'Calculating...',
      categoryBreakdown: {
        attractions: 'Calculating...',
        foodAndDining: 'Calculating...',
        transportation: 'Calculating...',
        shoppingAndMisc: 'Calculating...',
        buffer: 'Calculating...'
      }
    }
  }

  // Require a savedItineraryId
  if (!tripDetails.savedItineraryId) {
    throw new Error('Cannot simulate itinerary without a savedItineraryId');
  }
  
  console.log('[ItineraryLoader] Loading saved itinerary:', tripDetails.savedItineraryId);
  console.log('[ItineraryLoader] Loading saved itinerary:', tripDetails);
  const savedItinerary = await loadItineraryById(tripDetails.savedItineraryId, onUpdate);
  
  if (!savedItinerary) {
    throw new Error(`Failed to load saved itinerary with ID: ${tripDetails.savedItineraryId}`);
  }
  
  return savedItinerary;
}

export async function loadItineraryById(
  itineraryId: string,
  onUpdate?: (partialItinerary: Partial<Itinerary>, isStreaming?: boolean, activeDay?: number) => void
): Promise<Itinerary | null> {
  try {
    // Find saved itinerary
    const allItineraries = await itineraryService.getAllItineraries();
    const savedItinerary = allItineraries.find(i => i.id === itineraryId);

    console.log('[ItineraryLoader] Loading All Itinerararies', typeof(allItineraries), allItineraries);
    console.log('[ItineraryLoader] Loading Saved Itinerararies', savedItinerary);

    if (!savedItinerary) return null;
    
    // Create structure with force flags but DON'T SAVE it
    const markedItinerary = {
      ...JSON.parse(JSON.stringify(savedItinerary)),
      _forceShowResults: true,
      shouldShowResults: true
    };
    
    // Initial update with basic info - FOR UI ONLY
    if (onUpdate) {
      const initialData = {
        id: markedItinerary.id,
        tripDetails: markedItinerary.tripDetails,
        days: [],
        budgetSummary: markedItinerary.budgetSummary // Use existing budget, not default
      };
      onUpdate(initialData, false);
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    // Progressive UI updates but don't save anything
    // Progressive loading of days and activities
    if (markedItinerary.days?.length && onUpdate) {
      const buildingItinerary = {
        ...markedItinerary,
        days: []
      };
      
      // Add each day with delay
      for (const day of markedItinerary.days) {
        const activeDay = day.dayNumber;
        buildingItinerary.days.push({...day, activities: []});
        
        // Add activities progressively
        if (day.activities?.length) {
          for (const activity of day.activities) {
            const dayIndex = buildingItinerary.days.findIndex(d => d.dayNumber === day.dayNumber);
            if (dayIndex !== -1) {
              buildingItinerary.days[dayIndex].activities = [
                ...(buildingItinerary.days[dayIndex].activities || []),
                activity
              ];
              
              // Update with streaming flag for animation
              onUpdate(buildingItinerary, true, activeDay);
              await new Promise(resolve => setTimeout(resolve, 400));
            }
          }
        }
      }
    }
    // Only set as current, don't save again
    itineraryService.setCurrentItinerary(itineraryId);
    
    console.log('[ItineraryLoader] Itinerary Loading Has Been Finally Completededded ------------------ :', savedItinerary);

    window.dispatchEvent(new CustomEvent('itinerary-budget-started'));

    return savedItinerary as Itinerary;
  } catch (error) {
    console.error('[ItineraryLoader] Error:', error);
    return null;
  }
}

// Generate prompt for a specific chunk of days

